/**
 * Prospector bootstrap metadata (GGCC / kryspace alignment).
 * Keys are snake_case to match `POST .../chat/threads/bootstrap` and thread rows.
 */

import { kryspaceCompanySlugHint } from './kryspaceSlugHint';
import {
  landingSourceKeyToMetadata,
  readPersistedLandingSourceKey,
  type LandingSourceKey,
} from './landingSourceKey';

/** `metadata.prospecting_delivery` when the SPA hosts the prospector session. */
export const PROSPECTING_DELIVERY_KRYSALIS_PLATFORM_SPA = 'krysalis_platform_spa' as const;

export interface BuildProspectingBootstrapMetadataParams {
  companyId: string;
  companyDisplayName?: string | null;
  /**
   * Claude project id or kryspace company slug — ties platform thread to Claude / disk workspace.
   * Optional; operators can also set sessionStorage (see PROSPECTING_PLATFORM_RUNBOOK).
   */
  claudeProjectRef?: string | null;
  /** Landing source_key; defaults to sessionStorage capture when omitted. */
  sourceKey?: LandingSourceKey | null;
}

/**
 * Slice of `metadata` for `ui_role: prospector` bootstrap (merge with model, name, etc.).
 */
export function buildProspectingBootstrapMetadata(
  params: BuildProspectingBootstrapMetadataParams
): Record<string, string> {
  const out: Record<string, string> = {
    prospecting_delivery: PROSPECTING_DELIVERY_KRYSALIS_PLATFORM_SPA,
    company_id: params.companyId,
  };
  const name = params.companyDisplayName?.trim();
  if (name) {
    out.kryspace_company_slug_hint = kryspaceCompanySlugHint(name);
  }
  const ref = params.claudeProjectRef?.trim();
  if (ref) {
    out.claude_project_ref = ref;
  }
  const sourceKey =
    params.sourceKey !== undefined ? params.sourceKey : readPersistedLandingSourceKey();
  const sourceMeta = landingSourceKeyToMetadata(sourceKey);
  Object.assign(out, sourceMeta);
  if (Object.keys(sourceMeta).length > 0) {
    out.krybot_instructions = JSON.stringify({
      prospect_code: sourceMeta.prospect_code,
      source_magnet: sourceMeta.source_magnet,
      source_channel: sourceMeta.source_channel,
      source_copy_variant: sourceMeta.source_copy_variant,
      target_product: sourceMeta.target_product,
      target_icp: sourceMeta.target_icp,
      conversation_starter: sourceMeta.conversation_starter,
      market_fit_rubric: sourceMeta.market_fit_rubric,
      referral_partner: sourceMeta.referral_partner,
    });
  }
  return out;
}

/** Known traceability keys on thread or merged message metadata. */
export interface KrybotTraceabilityFields {
  claude_project_ref?: string;
  kryspace_company_slug_hint?: string;
  company_id?: string;
  prospecting_delivery?: string;
}

/**
 * Read GGCC/kryspace trace fields from API metadata (merged DB + llm_metadata).
 */
export function extractKrybotTraceabilityFromMetadata(
  metadata: Record<string, unknown> | null | undefined
): KrybotTraceabilityFields {
  if (!metadata || typeof metadata !== 'object') {
    return {};
  }
  const pick = (key: string): string | undefined => {
    const v = metadata[key];
    return typeof v === 'string' && v.trim() ? v.trim() : undefined;
  };
  return {
    claude_project_ref: pick('claude_project_ref'),
    kryspace_company_slug_hint: pick('kryspace_company_slug_hint'),
    company_id: pick('company_id'),
    prospecting_delivery: pick('prospecting_delivery'),
  };
}
